import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Alert,
    Box,
    Drawer,
    IconButton,
    Typography,
    Divider,
} from '@mui/material';
import { Close, Http } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { DynamicContainer } from '../common/DynamicContainer';
import {
    mcpEndpointColumns,
    mcpEndpointFormFieldsForGlobalTool,
    prepareMcpEndpointEditData,
    fetchMcpCatalogEndpoints,
    createMcpCatalogEndpoint,
    updateMcpCatalogEndpoint,
    deleteMcpCatalogEndpoint,
    buildMcpEndpointInitialFormForTool,
    resolveConnectorKeyForTool,
    isCdxUser,
} from '../common/mcpEndpointCatalogConfig';

const toolIdOf = (tool) => tool?.toolId ?? tool?.tool_id;
const mcIdOf = (tool) => tool?.mcId ?? tool?.mc_id;

const DRAWER_WIDTH = 'min(1100px, 96vw)';

const GlobalToolMcpEndpointsDrawer = ({ open, onClose, tool, masterConnectors = [] }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const connectorKey = useMemo(
        () => resolveConnectorKeyForTool(tool, masterConnectors),
        [tool, masterConnectors]
    );

    const loadRows = useCallback(async () => {
        setLoading(true);
        try {
            const globalToolId = toolIdOf(tool);
            const data = await fetchMcpCatalogEndpoints({
                globalToolId: globalToolId ?? undefined,
            });
            setRows(data);
        } catch (error) {
            console.error(error);
            toast.error(`Error fetching MCP endpoints: ${error.message}`);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [tool]);

    useEffect(() => {
        if (open && tool) {
            loadRows();
        }
    }, [open, tool, loadRows]);

    const gridRows = useMemo(() => {
        const sorted = [...rows].sort((a, b) =>
            String(a.mec_operation_key || '').localeCompare(String(b.mec_operation_key || ''), undefined, {
                sensitivity: 'base',
            })
        );
        return sorted.map((item, index) => ({
            ...item,
            id: item.mec_id || `mec-${index}`,
        }));
    }, [rows]);

    const initialFormData = useMemo(
        () => buildMcpEndpointInitialFormForTool(tool, connectorKey),
        [tool, connectorKey]
    );

    const withToolIds = (data) => ({
        ...data,
        mec_global_tool_id: toolIdOf(tool) ?? data.mec_global_tool_id,
        mec_mc_id: mcIdOf(tool) || data.mec_mc_id,
        mec_connector_key: connectorKey || data.mec_connector_key,
    });

    const handleCreate = async (data) => {
        await createMcpCatalogEndpoint(withToolIds(data));
        await loadRows();
    };

    const handleUpdate = async (id, data) => {
        await updateMcpCatalogEndpoint(id, withToolIds(data));
        await loadRows();
    };

    const handleDelete = async (id) => {
        try {
            await deleteMcpCatalogEndpoint(id);
            await loadRows();
        } catch (error) {
            toast.error(`Error deleting MCP endpoint: ${error.message}`);
            throw error;
        }
    };

    if (!tool) return null;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: DRAWER_WIDTH, maxWidth: '100%' },
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="h6" component="div">
                        MCP Endpoints — {tool.toolName}
                    </Typography>
                    <IconButton onClick={onClose} aria-label="Close">
                        <Close />
                    </IconButton>
                </Box>

                <Alert severity="info" sx={{ mx: 2, mt: 1.5 }}>
                    Endpoints linked to this tool by <strong>global_tool_id</strong>
                    {toolIdOf(tool) != null ? ` (${toolIdOf(tool)})` : ''}
                    {mcIdOf(tool) ? ` and connector mc_id (${mcIdOf(tool)})` : ''}.
                    New endpoints created here are stored with those IDs (not connector name only).
                    Enable per tenant under <strong>Tenant Tools</strong> (MCP endpoints are enabled automatically).
                </Alert>

                <Divider sx={{ mt: 1 }} />

                <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <DynamicContainer
                        title="MCP Endpoints"
                        icon={Http}
                        columns={mcpEndpointColumns}
                        formFields={mcpEndpointFormFieldsForGlobalTool}
                        initialFormData={initialFormData}
                        idField="id"
                        rows={gridRows}
                        onCreate={handleCreate}
                        onUpdate={handleUpdate}
                        onDelete={handleDelete}
                        prepareEditData={prepareMcpEndpointEditData}
                        isLoading={loading}
                        isCdxUser={isCdxUser()}
                    />
                </Box>
            </Box>
        </Drawer>
    );
};

export default GlobalToolMcpEndpointsDrawer;
